import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { Autocomplete } from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Context } from "../context/FittingContext";
import RadioInput from "./RadioInput";
import TextInput from "./TextInput";

const FittingInformationDialog = ({ open, dialogControl, dataFilled }) => {
	const { updateClubType, clubState } = useContext(Context);
	// eslint-disable-next-line
	const [sessionId, setSessionId] = useState(
		localStorage.getItem("sessionId") || uuidv4()
	);
	const [fitterName, setFitterName] = useState(
		localStorage.getItem("fitterName") || ""
	);
	const [locationId, setLocationId] = useState(
		localStorage.getItem("locationId") || ""
	);
	const [rightHanded, setRightHanded] = useState(
		localStorage.getItem("rightHanded") || ""
	);
	const [clubType, setClubType] = useState(
		localStorage.getItem("clubType") || ""
	);
	const [handicap, setHandicap] = useState(
		localStorage.getItem("handicap") || 0
	);
	const [gender, setGender] = useState(
		localStorage.getItem("gender") || ""
	);
	const [clubSubType, setClubSubType] = useState(
		localStorage.getItem("clubSubType") || ""
	);
	const [hasSavedData, setHasSavedData] = useState(
		localStorage.getItem("sessionData") ? true : false
	);
	const [displayError, setDisplayError] = useState(false);

	// Reset state from localStorage when dialog opens
	useEffect(() => {
		if (open) {
			setSessionId(localStorage.getItem("sessionId") || uuidv4());
			setFitterName(localStorage.getItem("fitterName") || "");
			setLocationId(localStorage.getItem("locationId") || "");
			setRightHanded(localStorage.getItem("rightHanded") || "");
			setClubType(localStorage.getItem("clubType") || "");
			setClubSubType(localStorage.getItem("clubSubType") || "");
			setHandicap(localStorage.getItem("handicap") || 0);
			setGender(localStorage.getItem("gender") || "");
			setHasSavedData(localStorage.getItem("sessionData") ? true : false);
			setDisplayError(false);
		}
	}, [open]);

	// Set default subtype when club type changes
	useEffect(() => {
		if (clubType === "Wedge") {
			setClubSubType("Sand Wedge");
		} else if (clubType === "Hybrid") {
			setClubSubType("3-Hybrid");
		} else if (clubType === "Wood") {
			setClubSubType("3-Wood");
		} else {
			setClubSubType("");
		}
	}, [clubType]);
	return (
		<Dialog
			sx={{ "& .MuiDialog-paper": { width: "100%" } }}
			maxWidth="xs"
			open={open}
		>
			<DialogTitle sx={{ paddingBottom: "0px" }}>
				Session Information
			</DialogTitle>
			<DialogContent dividers>
				<Box justifyContent="center" display="flex">
					<Typography align="center" sx={{ color: "red" }}>
						*
					</Typography>
					<Typography align="center" sx={{ fontStyle: "italic" }}>
						Inidicates a required field
					</Typography>
				</Box>
				{hasSavedData ? (
					<Alert severity="warning">
						There is saved swing data from the prior session. Updating the
						session information will remove this data
					</Alert>
				) : null}

				{displayError ? (
					<Alert severity="error">Please fill out all required fields</Alert>
				) : null}

				<TextInput
					label="Session ID"
					value={sessionId}
					variant="filled"
					InputProps={{
						disableUnderline: true,
						style: { borderRadius: "4px" },
					}}
					inputProps={{
						style: { paddingTop: "15px" },
					}}
					disabled
					fullWidth
				/>
				<Autocomplete
					disablePortal
					id="fitter-name"
					options={clubState.fitterInfo}
					onChange={(event, newValue) => {
						setFitterName(newValue);
					}}
					renderInput={(params) => (
						<TextInput label="Fitter Name" asterisk {...params} />
					)}
					value={fitterName}
				/>
				<Autocomplete
					disablePortal
					id="location-id"
					options={clubState.locationInfo}
					onChange={(event, newValue) => {
						setLocationId(newValue);
					}}
					renderInput={(params) => (
						<TextInput label="Location ID" asterisk {...params} />
					)}
					value={locationId}
				/>
				<RadioInput
					label="Dexterity"
					asterisk
					value={rightHanded}
					onChange={(e) => setRightHanded(e.target.value)}
					options={[
						{ value: false, label: "Left" },
						{ value: true, label: "Right" },
					]}
				/>
				<RadioInput
					key={`gender-${gender}-${sessionId}`}
					label="Gender"
					asterisk
					value={gender}
					onChange={(e) => setGender(e.target.value)}
					options={[
						{ value: "Male", label: "Male" },
						{ value: "Female", label: "Female" },
					]}
				/>

				<Box sx={{ marginBottom: 2 }}>
					<Typography variant="body2" sx={{ marginBottom: 1 }}>
						Club Type <span style={{ color: 'red' }}>*</span>
					</Typography>
					<ToggleButtonGroup
						value={clubType}
						exclusive
						onChange={(event, newValue) => {
							if (newValue !== null) {
								setClubType(newValue);
							}
						}}
						aria-label="club type"
						fullWidth
					>
						<ToggleButton value="Drivers" aria-label="drivers">
							Driver
						</ToggleButton>
						<ToggleButton value="Irons" aria-label="irons">
							Iron
						</ToggleButton>
						<ToggleButton value="Hybrid" aria-label="hybrid">
							Hybrid
						</ToggleButton>
						<ToggleButton value="Wood" aria-label="wood">
							Wood
						</ToggleButton>
						<ToggleButton value="Wedge" aria-label="wedge">
							Wedge
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>

				{/* Club SubType Selection */}
				{clubType === "Wedge" && (
					<Box sx={{ marginBottom: 2 }}>
						<ToggleButtonGroup
							value={clubSubType}
							exclusive
							onChange={(event, newValue) => {
								if (newValue !== null) {
									setClubSubType(newValue);
								}
							}}
							aria-label="club subtype"
							fullWidth
						>
							<ToggleButton value="Pitching Wedge" aria-label="pitching wedge">
								Pitching
							</ToggleButton>
							<ToggleButton value="Sand Wedge" aria-label="sand wedge">
								Sand
							</ToggleButton>
							<ToggleButton value="Lob Wedge" aria-label="lob wedge">
								Lob
							</ToggleButton>
							<ToggleButton value="Gap Wedge" aria-label="gap wedge">
								Gap
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				)}

				{clubType === "Hybrid" && (
					<Box sx={{ marginBottom: 2 }}>
						<ToggleButtonGroup
							value={clubSubType}
							exclusive
							onChange={(event, newValue) => {
								if (newValue !== null) {
									setClubSubType(newValue);
								}
							}}
							aria-label="club subtype"
							fullWidth
						>
							<ToggleButton value="1-Hybrid" aria-label="1-hybrid">
								1
							</ToggleButton>
							<ToggleButton value="2-Hybrid" aria-label="2-hybrid">
								2
							</ToggleButton>
							<ToggleButton value="3-Hybrid" aria-label="3-hybrid">
								3
							</ToggleButton>
							<ToggleButton value="4-Hybrid" aria-label="4-hybrid">
								4
							</ToggleButton>
							<ToggleButton value="5-Hybrid" aria-label="5-hybrid">
								5
							</ToggleButton>
							<ToggleButton value="6-Hybrid" aria-label="6-hybrid">
								6
							</ToggleButton>
							<ToggleButton value="7-Hybrid" aria-label="7-hybrid">
								7
							</ToggleButton>
							<ToggleButton value="8-Hybrid" aria-label="8-hybrid">
								8
							</ToggleButton>
							<ToggleButton value="9-Hybrid" aria-label="9-hybrid">
								9
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				)}

				{clubType === "Wood" && (
					<Box sx={{ marginBottom: 2 }}>
						<ToggleButtonGroup
							value={clubSubType}
							exclusive
							onChange={(event, newValue) => {
								if (newValue !== null) {
									setClubSubType(newValue);
								}
							}}
							aria-label="club subtype"
							fullWidth
						>
							<ToggleButton value="2-Wood" aria-label="2-wood">
								2
							</ToggleButton>
							<ToggleButton value="3-Wood" aria-label="3-wood">
								3
							</ToggleButton>
							<ToggleButton value="4-Wood" aria-label="4-wood">
								4
							</ToggleButton>
							<ToggleButton value="5-Wood" aria-label="5-wood">
								5
							</ToggleButton>
							<ToggleButton value="6-Wood" aria-label="6-wood">
								6
							</ToggleButton>
							<ToggleButton value="7-Wood" aria-label="7-wood">
								7
							</ToggleButton>
							<ToggleButton value="8-Wood" aria-label="8-wood">
								8
							</ToggleButton>
							<ToggleButton value="9-Wood" aria-label="9-wood">
								9
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				)}
				<TextInput
					label="Player Handicap"
					value={handicap}
					onChange={(e) => {
						if (!isNaN(e.target.value.trim())) {
							setHandicap(e.target.value.trim());
						}
					}}
					fullWidth
				/>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => {
						dialogControl(false);
						setDisplayError(false);
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={() => {
						if (
							sessionId &&
							fitterName &&
							locationId &&
							rightHanded &&
							gender &&
							clubType
						) {
							localStorage.setItem("sessionId", sessionId);
							localStorage.setItem("fitterName", fitterName);
							localStorage.setItem("locationId", locationId);
							localStorage.setItem("rightHanded", rightHanded);
							localStorage.setItem("gender", gender);
							localStorage.setItem("clubType", clubType);
							localStorage.setItem("clubSubType", clubSubType);
							if (handicap === "") {
								localStorage.setItem("handicap", 0);
							} else {
								localStorage.setItem("handicap", handicap);
							}
							localStorage.removeItem("sessionData");
							setHasSavedData(false);
							dialogControl(false);
							setDisplayError(false);
							dataFilled(true);
							updateClubType(clubType);
						} else {
							setDisplayError(true);
						}
					}}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default FittingInformationDialog;
